import { Component, Input, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { CommonModule } from '@angular/common';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { Item } from '../../models/item.model';
import { availableDocuments, availableFormats } from '../../constants/constants';
import { IonSpinner, IonList, IonItem, IonLabel, IonButton, IonIcon, IonHeader, IonToolbar, IonTitle, IonContent, IonButtons, IonFooter } from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import { checkmarkCircle, close } from 'ionicons/icons';

@Component({
  selector: 'app-confirmation-modal',
  templateUrl: './confirmation-modal.component.html',
  styleUrls: ['./confirmation-modal.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    TranslatePipe,
    IonSpinner, IonList, IonItem, IonLabel, IonButton, IonIcon,
    IonHeader, IonToolbar, IonTitle, IonContent, IonButtons, IonFooter
  ]
})
export class ConfirmationModalComponent implements OnInit {
  @Input() title: string = '';
  @Input() message: string = '';
  @Input() confirmButtonText: string = 'CONFIRM';
  @Input() cancelButtonText: string = 'CANCEL';
  @Input() isError: boolean = false;
  @Input() isSuccessfulRegistration: boolean = false;
  @Input() showDocumentUpload: boolean = false;
  @Input() item: Item | null = null; // JAVÍTÁS 1: @Input() hozzáadva
  @Input() isUploading: boolean = false;
  @Input() isProcessing: boolean = false;

  selectedFiles: { [key: string]: File } = {};
  isDocumentsValid = false;
  format = availableFormats;
  public documentEntries: Array<{ key: string, value: string[] }> = [];

  public get confirmButtonColor(): 'success' | 'danger' {
    const positiveActions = ['SUBSCRIBE', 'CONFIRM', 'OK', 'SAVE', 'SAVE_CHANGES', 'UPLOAD_CV', 'APPROVE'];
    return positiveActions.includes(this.confirmButtonText) ? 'success' : 'danger';
  }

  constructor(private modalController: ModalController) {
    addIcons({ checkmarkCircle, close });
  }

  ngOnInit() {
    this.prepareDocumentEntries();
  }

  private prepareDocumentEntries(): void {
    if (this.item?.documentValues && typeof this.item.documentValues === 'object') {
      this.documentEntries = Object.entries(this.item.documentValues).map(([key, value]) => ({
        key,
        value: Array.isArray(value) ? value : []
      }));
    } else {
      this.documentEntries = [];
    }
  }

  getDocumentName(docType: string): string {
    const docObj = availableDocuments.find(doc => doc.value === docType);
    return docObj ? docObj.name : docType;
  }

  onFileSelected(event: Event, docType: string) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) {
      const extension = file.name.split('.').pop()?.toLowerCase() || '';
      const allowedFormats = this.item?.documentValues?.[docType] || [];
      if (allowedFormats.includes('any') || allowedFormats.includes(extension)) {
        this.selectedFiles[docType] = file;
      } else {
        delete this.selectedFiles[docType];
      }
      this.validateDocuments();
    }
  }

  private validateDocuments() {
    if (!this.item?.documentValues) {
      this.isDocumentsValid = true;
      return;
    }
    const requiredDocTypes = Object.keys(this.item.documentValues);
    this.isDocumentsValid = requiredDocTypes.every(docType => {
      const file = this.selectedFiles[docType];
      if (!file) return false;

      const extension = file.name.split('.').pop()?.toLowerCase() || '';
      const allowedFormats = this.item?.documentValues[docType] || [];
      return allowedFormats.includes('any') || allowedFormats.includes(extension);
    });
  }

  getAllowedFormatsText(docType: string): string {
    const formatValues = this.item?.documentValues?.[docType] || [];
    return formatValues.map(formatValue => {
      const formatObj = this.format.find(f => f.value === formatValue);
      return formatObj ? formatObj.name : formatValue;
    }).join(', ');
  }

  private formatToMimeType(format: string): string {
    switch (format.toLowerCase()) {
      case 'pdf': return 'application/pdf';
      case 'png': return 'image/png';
      case 'jpg': return 'image/jpeg';
      case 'jpeg': return 'image/jpeg';
      case 'docx': return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      case 'pptx': return 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
      default: return format;
    }
  }

  getAllowedFormatsForInput(docType: string): string {
    const formats = this.item?.documentValues?.[docType] || [];
    if (formats.includes('any')) {
      return '*';
    }
    return formats.map(format => this.formatToMimeType(format)).join(',');
  }

  onConfirm(): void {
    if (this.showDocumentUpload && !this.isDocumentsValid) {
      return;
    }
    this.modalController.dismiss({
      confirmed: true,
      files: this.showDocumentUpload ? this.selectedFiles : undefined,
    });
  }

  onCancel(): void {
    this.modalController.dismiss({
      confirmed: false
    });
  }
}